import { useCallback, useState, type FormEvent } from 'react';
import { Mail, MapPin, Clock, ArrowRight } from 'lucide-react';
import { Section } from '../components/Section';
import { TurnstileWidget } from '../components/TurnstileWidget';

const BFF_URL = import.meta.env.VITE_BFF_BASE_URL || 'https://bff.redbax.io';
// Cloudflare's public "always passes" test key, so local dev works without configuration.
// The real key is injected at build time — see .env.example.
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';
const endpointOptions = ['1-50', '51-200', '201-500', '500+'];

export function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  // Bumping this remounts the widget, which is how we ask Cloudflare for a fresh token.
  const [turnstileKey, setTurnstileKey] = useState(0);

  const handleTurnstileError = useCallback(
    () => setError('Could not load the bot check. Please refresh, or email us directly.'),
    [],
  );

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    const form = e.currentTarget;
    const data = {
      name: (form.elements.namedItem('name') as HTMLInputElement).value,
      email: (form.elements.namedItem('email') as HTMLInputElement).value,
      company: (form.elements.namedItem('company') as HTMLInputElement).value,
      role: (form.elements.namedItem('role') as HTMLInputElement).value || null,
      endpoints: (form.elements.namedItem('endpoints') as HTMLSelectElement).value,
      message: (form.elements.namedItem('message') as HTMLTextAreaElement).value || null,
      website: (form.elements.namedItem('website') as HTMLInputElement).value || null,
      turnstileToken,
    };

    try {
      const res = await fetch(`${BFF_URL}/bff/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (res.status === 429) {
        setError('Too many submissions. Please try again later.');
      } else if (res.status === 403) {
        // Turnstile tokens are single-use and short-lived, so a stale one lands here.
        setTurnstileToken('');
        setTurnstileKey((k) => k + 1);
        setError('Your bot check expired. Please complete it again and resubmit.');
      } else if (!res.ok) {
        setError('Something went wrong. Please email us directly.');
      } else {
        setSubmitted(true);
      }
    } catch {
      setError('Could not reach our server. Please email us directly.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden px-6 pb-8 pt-20 md:pb-12 md:pt-28">
        <div className="pointer-events-none absolute -top-32 right-1/3 h-[500px] w-[500px] rounded-full bg-red-accent/5 blur-3xl" />
        <div className="relative mx-auto max-w-3xl text-center">
          <h1 className="animate-fade-in-up text-4xl font-extrabold leading-tight tracking-tight md:text-6xl">
            Let&apos;s{' '}
            <span className="bg-gradient-to-r from-red-accent to-cyan-info bg-clip-text text-transparent">
              talk
            </span>
          </h1>
          <p className="animate-fade-in-up animate-delay-100 mx-auto mt-6 max-w-xl text-lg text-text-secondary">
            Register your interest, ask a question, or tell us about your data protection requirements.
          </p>
        </div>
      </section>

      {/* Contact form + info */}
      <Section className="pt-12">
        <div className="grid gap-12 md:grid-cols-5">
          {/* Form */}
          <div className="md:col-span-3">
            {submitted ? (
              <div className="flex flex-col items-center rounded-2xl border border-green-ok/30 bg-surface p-12 text-center">
                <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-green-ok/10">
                  <ArrowRight size={32} className="text-green-ok" />
                </div>
                <h3 className="text-2xl font-bold text-text-primary">Thank you</h3>
                <p className="mt-3 text-text-secondary">
                  We have received your message and will respond within one business day.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="name"
                      className="mb-2 block text-sm font-medium text-text-primary"
                    >
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full rounded-lg border border-border bg-surface px-4 py-3 text-sm text-text-primary placeholder:text-text-secondary/50 outline-none transition-colors focus:border-red-accent"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="mb-2 block text-sm font-medium text-text-primary"
                    >
                      Work Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full rounded-lg border border-border bg-surface px-4 py-3 text-sm text-text-primary placeholder:text-text-secondary/50 outline-none transition-colors focus:border-red-accent"
                      placeholder="you@company.com"
                    />
                  </div>
                </div>

                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="company"
                      className="mb-2 block text-sm font-medium text-text-primary"
                    >
                      Company
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      required
                      className="w-full rounded-lg border border-border bg-surface px-4 py-3 text-sm text-text-primary placeholder:text-text-secondary/50 outline-none transition-colors focus:border-red-accent"
                      placeholder="Company name"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="role"
                      className="mb-2 block text-sm font-medium text-text-primary"
                    >
                      Role
                    </label>
                    <input
                      type="text"
                      id="role"
                      name="role"
                      className="w-full rounded-lg border border-border bg-surface px-4 py-3 text-sm text-text-primary placeholder:text-text-secondary/50 outline-none transition-colors focus:border-red-accent"
                      placeholder="Your role"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="endpoints"
                    className="mb-2 block text-sm font-medium text-text-primary"
                  >
                    Number of endpoints
                  </label>
                  <select
                    id="endpoints"
                    name="endpoints"
                    required
                    className="w-full rounded-lg border border-border bg-surface px-4 py-3 text-sm text-text-primary outline-none transition-colors focus:border-red-accent"
                    defaultValue=""
                  >
                    <option value="" disabled>
                      Select range
                    </option>
                    {endpointOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="mb-2 block text-sm font-medium text-text-primary"
                  >
                    Message{' '}
                    <span className="font-normal text-text-secondary">(optional)</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    className="w-full resize-none rounded-lg border border-border bg-surface px-4 py-3 text-sm text-text-primary placeholder:text-text-secondary/50 outline-none transition-colors focus:border-red-accent"
                    placeholder="Tell us about your requirements..."
                  />
                </div>

                {/* Honeypot */}
                <input
                  type="text"
                  name="website"
                  autoComplete="off"
                  tabIndex={-1}
                  className="absolute -left-[9999px] h-0 w-0 opacity-0"
                />

                <TurnstileWidget
                  key={turnstileKey}
                  siteKey={TURNSTILE_SITE_KEY}
                  onToken={setTurnstileToken}
                  onError={handleTurnstileError}
                />

                {error && (
                  <p className="text-sm text-red-accent">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting || !turnstileToken}
                  className="inline-flex items-center gap-2 rounded-lg bg-red-accent px-8 py-3.5 text-base font-semibold text-white transition-colors hover:bg-red-hover disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Register Interest'}
                  {!submitting && <ArrowRight size={18} />}
                </button>
              </form>
            )}
          </div>

          {/* Info side */}
          <div className="md:col-span-2">
            <div className="rounded-2xl border border-border bg-surface p-8">
              <h3 className="text-xl font-bold text-text-primary">Get in touch</h3>

              <div className="mt-8 space-y-6">
                <div className="flex items-start gap-4">
                  <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-subtle">
                    <Mail size={18} className="text-red-accent" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-primary">Email</p>
                    <a
                      href="mailto:sales@redbax.io"
                      className="mt-1 text-sm text-text-secondary transition-colors hover:text-red-accent"
                    >
                      sales@redbax.io
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-subtle">
                    <Clock size={18} className="text-red-accent" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-primary">Response time</p>
                    <p className="mt-1 text-sm text-text-secondary">
                      We&apos;ll respond within one business day.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-subtle">
                    <MapPin size={18} className="text-red-accent" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-primary">Location</p>
                    <p className="mt-1 text-sm text-text-secondary">London, United Kingdom</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 border-t border-border pt-6">
                <p className="text-sm leading-relaxed text-text-secondary">
                  Looking for technical support? Existing customers can reach our support team
                  directly through the dashboard.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
}
